import React from 'react';

export default function PrivacyPolicy() {
  const s = {
    page: { minHeight: '100vh', background: '#F5F8FC', paddingTop: 100, paddingBottom: 60 },
    wrap: { maxWidth: 800, margin: '0 auto', padding: '0 24px' },
    title: { fontSize: 32, fontWeight: 700, color: '#1B3A5C', marginBottom: 8 },
    updated: { fontSize: 14, color: '#6B7B8D', marginBottom: 32 },
    h2: { fontSize: 20, fontWeight: 600, color: '#1B3A5C', marginTop: 32, marginBottom: 12 },
    p: { fontSize: 15, lineHeight: 1.75, color: '#333', marginBottom: 12 },
    ul: { paddingLeft: 24, marginBottom: 12 },
    li: { fontSize: 15, lineHeight: 1.75, color: '#333', marginBottom: 4 },
    a: { color: '#4A9BD9', textDecoration: 'none' },
    footer: { marginTop: 48, paddingTop: 24, borderTop: '1px solid #D6E4F0', fontSize: 14, color: '#6B7B8D' }
  };

  return (
    <div style={s.page}>
      <div style={s.wrap}>
        <h1 style={s.title}>Privacy Policy</h1>
        <p style={s.updated}>Effective Date: 1 January 2026</p>

        <h2 style={s.h2}>1. Who We Are</h2>
        <p style={s.p}>
          Global Mind Wellness Centre ("GMW," "we," "us," or "our") operates a hybrid
          AI-human telemedicine platform at <a style={s.a} href="https://globalmindwellness.org">globalmindwellness.org</a>.
          We are registered in Eldoret, Kenya, and our Data Protection Officer can be
          reached at <a style={s.a} href="mailto:care@globalmindwellness.org">care@globalmindwellness.org</a>.
        </p>

        <h2 style={s.h2}>2. Information We Collect</h2>
        <p style={s.p}>We collect the following categories of personal data:</p>
        <ul style={s.ul}>
          <li style={s.li}><strong>Identity data:</strong> full name, email address, phone number.</li>
          <li style={s.li}><strong>Health data (special category):</strong> mental health history,
            symptoms disclosed during AI intake conversations, clinical notes, prescriptions,
            and appointment records.</li>
          <li style={s.li}><strong>Payment data:</strong> transaction IDs and amounts processed
            through PayPal or M-Pesa (Safaricom Daraja). We do not store card details; payment
            processors handle that under their own PCI-DSS compliance.</li>
          <li style={s.li}><strong>Technical data:</strong> IP address, browser type, device
            information, and session cookies necessary for platform operation.</li>
          <li style={s.li}><strong>Referral data:</strong> name and contact details of referral
            partners and the clients they refer, as provided by the referring party.</li>
        </ul>

        <h2 style={s.h2}>3. Legal Basis for Processing</h2>
        <p style={s.p}>Under the Kenya Data Protection Act 2019 (DPA), we process your data on the following grounds:</p>
        <ul style={s.ul}>
          <li style={s.li}><strong>Consent</strong> — you give explicit consent at registration and before AI intake sessions.</li>
          <li style={s.li}><strong>Contractual necessity</strong> — processing is necessary to deliver the telemedicine services you requested.</li>
          <li style={s.li}><strong>Vital interests</strong> — in crisis situations, we may process data to protect your life or safety.</li>
          <li style={s.li}><strong>Legal obligation</strong> — we process data to comply with KMPDC regulations, the DPA, and other applicable law.</li>
        </ul>

        <h2 style={s.h2}>4. How We Use Your Information</h2>
        <ul style={s.ul}>
          <li style={s.li}>Providing and managing telemedicine appointments.</li>
          <li style={s.li}>Running the AI intake assistant to gather preliminary information for your assigned clinician.</li>
          <li style={s.li}>Detecting crisis language and triggering appropriate safety responses.</li>
          <li style={s.li}>Communicating with you about appointments, payments, and account matters.</li>
          <li style={s.li}>Improving our platform and services.</li>
          <li style={s.li}>Complying with legal and regulatory obligations.</li>
        </ul>

        <h2 style={s.h2}>5. Data Sharing</h2>
        <p style={s.p}>We do not sell your data. We may share it only in these circumstances:</p>
        <ul style={s.ul}>
          <li style={s.li}><strong>Your assigned clinician</strong> — receives relevant intake summaries, clinical notes, and appointment history necessary to provide care.</li>
          <li style={s.li}><strong>Crisis/emergency</strong> — if our system detects imminent danger, we may share limited information with emergency services or a designated emergency contact.</li>
          <li style={s.li}><strong>Service providers</strong> — DeepSeek (AI), SMTP2GO (email), PayPal, and Safaricom Daraja process data as described in their respective privacy policies. We share only the minimum data required for each service to function.</li>
          <li style={s.li}><strong>Legal requirements</strong> — we may disclose data when required by a court order, KMPDC, the Office of the Data Protection Commissioner, or other lawful authority.</li>
        </ul>

        <h2 style={s.h2}>6. Data Security</h2>
        <p style={s.p}>
          We implement technical and organisational measures including TLS encryption for all
          data in transit, bcrypt hashing for passwords, role-based access controls, and secure
          hosting on Digital Ocean infrastructure. While no system is completely secure, we
          continually review and improve our safeguards.
        </p>

        <h2 style={s.h2}>7. Data Retention</h2>
        <ul style={s.ul}>
          <li style={s.li}><strong>Active patient records:</strong> retained for the duration of the therapeutic relationship plus 6 years, in line with KMPDC guidelines.</li>
          <li style={s.li}><strong>AI intake transcripts:</strong> retained for 90 days after the last associated session, then automatically deleted.</li>
          <li style={s.li}><strong>Payment records:</strong> retained for 7 years for tax and audit purposes.</li>
          <li style={s.li}><strong>Unused accounts:</strong> deleted after 12 months of inactivity and no completed appointments.</li>
        </ul>
        <p style={s.p}>
          You may request earlier deletion subject to legal retention obligations. Where we
          cannot delete (e.g., ongoing legal hold), we will restrict processing instead.
        </p>

        <h2 style={s.h2}>8. Your Rights Under the DPA</h2>
        <p style={s.p}>You have the right to:</p>
        <ul style={s.ul}>
          <li style={s.li}>Request access to your personal data.</li>
          <li style={s.li}>Request correction of inaccurate data.</li>
          <li style={s.li}>Request erasure of your data (subject to legal retention requirements).</li>
          <li style={s.li}>Restrict or object to processing.</li>
          <li style={s.li}>Request data portability in a structured, machine-readable format.</li>
          <li style={s.li}>Withdraw consent at any time (where processing is based on consent).</li>
        </ul>
        <p style={s.p}>
          To exercise any right, contact us at <a style={s.a} href="mailto:care@globalmindwellness.org">care@globalmindwellness.org</a>.
          We will respond within 21 days as required by the DPA. If you are unsatisfied, you
          may lodge a complaint with the <a style={s.a} href="https://odpc.go.ke" target="_blank" rel="noopener noreferrer">Office of the Data Protection Commissioner</a>.
        </p>

        <h2 style={s.h2}>9. Cookies</h2>
        <p style={s.p}>
          We use only essential cookies (session tokens for authentication). We do not use
          third-party advertising or tracking cookies.
        </p>

        <h2 style={s.h2}>10. Cross-Border Data Transfers</h2>
        <p style={s.p}>
          AI processing is handled by DeepSeek, which may process data outside Kenya. We
          ensure appropriate safeguards are in place. If we expand to serve clients in other
          jurisdictions, we will update this policy to address additional cross-border requirements
          (e.g., GDPR for EU clients).
        </p>

        <h2 style={s.h2}>11. Children's Data</h2>
        <p style={s.p}>
          Our services are not directed at persons under 18. We do not knowingly collect data
          from children. If we discover we have processed a child's data, we will delete it
          promptly.
        </p>

        <h2 style={s.h2}>12. Changes to This Policy</h2>
        <p style={s.p}>
          We may update this policy from time to time. Material changes will be communicated
          via email or a prominent notice on the platform. Your continued use after changes
          are posted constitutes acceptance of the revised policy.
        </p>

        <h2 style={s.h2}>13. Contact Us</h2>
        <p style={s.p}>
          <strong>Global Mind Wellness Centre</strong><br />
          Eldoret, Kenya<br />
          Phone: +254 711 381 420<br />
          WhatsApp: +254 711 381 420<br />
          Email: <a style={s.a} href="mailto:care@globalmindwellness.org">care@globalmindwellness.org</a>
        </p>

        <div style={s.footer}>
          &copy; 2026 Global Mind Wellness Centre. All rights reserved.
        </div>
      </div>
    </div>
  );
}
